import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {message} from 'antd';
import qs from 'qs';
import userInfoStore from '@/store/ServiceInfoStore';
interface ApiResponse<T = any> {
    code: string;
    message: string | {global?: string};
    data?: T;
    status?: number;
    result?: T;
}

const LOGIN_URLS = {
    qa: 'https://login.bcetest.baidu.com/?redirect=',
    online: 'https://login.bce.baidu.com/?redirect=',
} as const;

/**
 * 获取登录URL
 */
const getLoginUrl = (): string => {
    const isQa = location.hostname.includes('bcetest.baidu.com');
    return isQa ? LOGIN_URLS.qa : LOGIN_URLS.online;
};

/**
 * 获取Cookie值
 */
export const getCookie = (name: string): string | null => {
    const v = new RegExp('(^|;) ?' + name + '=([^;]*)(;|$)').exec(document.cookie);
    return v ? v[2] : null;
};

/**
 * 动态获取CSRF Token
 */
const getCsrfToken = (): string => {
    const buinfo = getCookie('bce-user-info');
    return buinfo ? buinfo.split('"')[1] : '';
};

/**
 * 创建axios实例
 */
const instance = axios.create({
    withCredentials: true,
    timeout: 30000,
});

/**
 * 获取Authorization Header
 */
const getAuthHeader = (): Record<string, string> => {
    if (userInfoStore?.userInfo?.access_token) {
        return {
            Authorization: `${userInfoStore.userInfo.token_type} ${userInfoStore.userInfo.access_token}`,
        };
    }
    return {};
};

// 请求拦截器
instance.interceptors.request.use(
    config => {
        const url = config.url || '';
        const csrfToken = getCsrfToken();
        const authHeaders = getAuthHeader();

        return {
            ...config,
            url,
            headers: {
                ...config.headers,
                Csrftoken: csrfToken,
                ...authHeaders,
            },
        };
    },
    error => Promise.reject(error)
);

// 响应拦截器
instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>): any => {
        const {data, status} = response;

        if (status === 200 && (data.code === '0' || data.status === 200)) {
            return data;
        }

        // 未授权，跳转登录
        if (data.code === '40100') {
            location.href = getLoginUrl() + encodeURIComponent(location.href);
            return Promise.reject(new Error('未授权，即将跳转登录'));
        }

        // 错误消息处理
        const errorMessage = data.message?.global || data.message || '请求失败';
        message.error(errorMessage);

        return Promise.reject(errorMessage);
    },
    error => {
        // Blob错误处理
        if (error.response?.data instanceof Blob) {
            return new Promise((_, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const errorData = JSON.parse(reader.result as string);
                        reject(new Error(errorData.message || '网络异常'));
                    } catch {
                        reject(error);
                    }
                };
                reader.onerror = () => reject(error);
                reader.readAsText(error.response.data);
            });
        }

        const errorMessage = error?.message || error.response?.data?.message || '网络异常';
        return Promise.reject(new Error(errorMessage));
    }
);

/**
 * GET请求
 */
export const get = <T = any>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> => {
    return instance.get(url, {params});
};

/**
 * POST JSON请求
 */
export const postJson = <T = any>(url: string, data: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return instance.post(url, data, {
        headers: {'Content-Type': 'application/json'},
        ...config,
    });
};

/**
 * POST表单请求
 */
export const post = (url: string, data: Record<string, any>): Promise<ApiResponse> => {
    return instance.post(url, qs.stringify(data));
};

/**
 * POST表单请求（文件上传）
 */
export const postForm = (url: string, data: any): Promise<AxiosResponse<Blob>> => {
    return instance.post(url, data, {
        responseType: 'blob',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
    });
};

/**
 * DELETE请求
 */
export const del = <T = any>(url: string, data?: Record<string, any>): Promise<ApiResponse<T>> => {
    return instance.delete(url, {params: data});
};

/**
 * 文件下载
 */
export const downloadFile = async (
    url: string,
    data: Record<string, any>,
    callback?: (error?: string) => void
): Promise<void> => {
    try {
        const response = await instance.post<Blob>(url, qs.stringify(data), {
            responseType: 'blob',
            headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        });

        // 创建下载链接
        const blobUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        callback?.();
    } catch (error: any) {
        console.error('下载失败:', error);
        callback?.(error?.message || '下载失败');
    }
};
